import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { adminUsersService } from "../../../services/adminUsers";
import { formatApiError } from "../../../services/api";

function generatePassword() {
  const alpha = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz";
  const nums = "23456789";
  const specials = "!@#$%&*";
  const pick = (src) => src[Math.floor(Math.random() * src.length)];
  const rest = [alpha, alpha, alpha, alpha, alpha, nums, nums, specials]
    .map(pick)
    .join("");
  return `MH${rest}`;
}

export function ResetPasswordModal({ open, onClose, user, onSuccess }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setShow(false);
      setErr(null);
    }
  }, [open]);

  const submit = async () => {
    if (password.length < 8 || !/\d/.test(password)) {
      setErr(t("admin.users.err_password"));
      return;
    }
    setBusy(true);
    try {
      await adminUsersService.resetPassword(user.id, password);
      toast.success(t("admin.users.reset_success"));
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e, t("common.error_generic")));
    } finally {
      setBusy(false);
    }
  };

  const autoGen = () => {
    // A18: generate the password but do NOT auto-copy to clipboard and
    // do NOT reveal the field. Recipient gets the new password via the
    // reset-notification email fired from the backend right after this
    // request succeeds.
    const pw = generatePassword();
    setPassword(pw);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-surface" data-testid="reset-password-modal">
        <DialogHeader>
          <DialogTitle>{t("admin.users.reset_title")}</DialogTitle>
          <DialogDescription>
            {t("admin.users.reset_body", { name: `${user.first_name} ${user.last_name}` })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label>{t("admin.users.field_password")}</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setErr(null); }}
                data-testid="reset-password-input"
                className="bg-surface border-border-subtle pr-10 font-mono"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                data-testid="reset-password-toggle"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={autoGen}
              data-testid="reset-password-generate"
              className="border-border-subtle"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {t("admin.users.generate_password")}
            </Button>
          </div>
          <p className="text-xs text-text-secondary">
            {t("admin.users.password_hint")}
          </p>
          {err && <p className="text-xs text-status-unavailable">{err}</p>}
        </div>
        <div className="mt-6 flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="reset-password-cancel">
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy}
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
            data-testid="reset-password-submit"
          >
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {t("admin.users.reset_confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
